import { createSelector } from 'reselect';
import type { IServiceProvider } from '@frontend/components/interfaces/serviceProvider';
import type { IReduxState } from '../../configureStore';
import type { DispatchType } from '../utils/dispatchType';
import { UrlBuilder } from '../../../common/utils/urlBuilder';
import { getJson } from '../fetch';
import { ReduxStatus } from '../utils/reduxStatus';
import { ServiceProvidersFilter } from './serviceProvidersFilter';

export enum serviceProviderActions {
    ERROR_SERVICE_PROVIDERS = 'ERROR_SERVICE_PROVIDERS',
    FETCH_SERVICE_PROVIDERS = 'FETCH_SERVICE_PROVIDERS',
    GET_SERVICE_PROVIDERS = 'GET_SERVICE_PROVIDERS',
}

export interface IServiceProviderPayload {
    authorization: { canAdd?: boolean; canUpdate?: boolean };
    serviceProviders: IServiceProvider[];
}

interface IServiceProvidersPayload {
    authorization: { canAdd?: boolean; canUpdate?: boolean };
    serviceProviders: IServiceProvider[];
}

interface IGetserviceProviders {
    payload: IServiceProvidersPayload;
    status: ReduxStatus.DONE;
    type: serviceProviderActions.GET_SERVICE_PROVIDERS;
}

function getserviceProviders(data: { authorization: {}; items: IServiceProvider[] }): IGetserviceProviders {
    return {
        type: serviceProviderActions.GET_SERVICE_PROVIDERS,
        status: ReduxStatus.DONE,
        payload: {
            authorization: data.authorization,
            serviceProviders: data.items,
        },
    };
}

interface IErrorserviceProviders {
    payload: IServiceProvidersPayload;
    status: ReduxStatus.ERROR;
    type: serviceProviderActions.ERROR_SERVICE_PROVIDERS;
}

function errorserviceProviders(): IErrorserviceProviders {
    return {
        type: serviceProviderActions.ERROR_SERVICE_PROVIDERS,
        status: ReduxStatus.ERROR,
        payload: {
            authorization: {},
            serviceProviders: [],
        },
    };
}

interface IFetchserviceProvidersSuccess {
    payload: IServiceProvidersPayload;
    status: ReduxStatus.LOADING;
    type: serviceProviderActions.FETCH_SERVICE_PROVIDERS;
}

function fetchserviceProvidersSuccess(): IFetchserviceProvidersSuccess {
    return {
        type: serviceProviderActions.FETCH_SERVICE_PROVIDERS,
        status: ReduxStatus.LOADING,
        payload: {
            authorization: {},
            serviceProviders: [],
        },
    };
}

const includedAuthorization = {
    includeItemAuthorizations: ['canUpdate'],
    includeListAuthorizations: ['canAdd'],
};

export function fetchserviceProviders(queryParameters?: IQueryParameters) {
    return async (dispatch: DispatchType) => {
        const urlBuilder = new UrlBuilder('/api/serviceProviders');
        if (queryParameters) {
            urlBuilder.add(queryParameters);
        }
        urlBuilder.add(includedAuthorization);
        dispatch(fetchserviceProvidersSuccess());

        const result = await getJson(urlBuilder.build());
        if (result.json) {
            dispatch(getserviceProviders(result.json));
        } else if (result.errorCode) {
            dispatch(errorserviceProviders());
        }
    };
}

export function serviceProvidersSelector(state: IReduxState) {
    return state.serviceProviders.data;
}

export function filterserviceProviderselector(queryParameters: IQueryParameters) {
    return createSelector(serviceProvidersSelector, (serviceProviderPayload) => {
        return applyserviceProviderFilters(queryParameters, serviceProviderPayload.serviceProviders);
    });
}

export function applyserviceProviderFilters(queryParameters: IQueryParameters, serviceProviders: IServiceProvider[]) {
    const filter = new ServiceProvidersFilter(serviceProviders);
    if (queryParameters.isActive) {
        filter.filterByActive();
    }
    if (queryParameters.employmentId) {
        filter.filterByEmploymentId(queryParameters.employmentId);
    }
    if (queryParameters.practitionerId) {
        filter.filterByPractitionerId(queryParameters.practitionerId);
    }
    if (queryParameters.serviceId) {
        filter.filterByServiceId(queryParameters.serviceId);
    }
    if (queryParameters.searchOrganization) {
        filter.filterByOrganizationName(queryParameters.searchOrganization);
    }
    if (queryParameters.organizationId) {
        filter.filterByOrganizationId(queryParameters.organizationId);
    }

    return filter.toArray();
}

export type ServiceProviderActionType = IGetserviceProviders | IErrorserviceProviders | IFetchserviceProvidersSuccess;

interface IQueryParameters {
    employmentId?: string;
    isActive?: true;
    organizationId?: string;
    practitionerId?: string;
    searchOrganization?: string;
    serviceId?: string;
}
